import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import './shared/layout.css';
import './HOVTab.css';
import { getCreators, canonKey, canonicalCreator } from './creators';
import AskScan from './scan/AskScan';
import CreatorManager from './scan/CreatorManager';
import PerfBlockHeader from './perf/PerfBlockHeader';
import { startScanExport } from './scanExport';
import { useScanExport, registerPresenter, claimScanExport, releaseScanExport, getLanded, takeOutcome, clearSummary, patchFailed as patchExportFailed } from './scanExportStore';

const API    = process.env.REACT_APP_API_URL || '';
// localStorage — since v4.11 the server (Supabase index) is the source of truth:
const LS_KEY = 'timbo_hov_v4';                                     // legacy list — READ-ONLY fallback, never written
const FOLDER_MAP_KEY = 'timbo_hov_folder_creators';                // legacy folder map — migrated once, then read-only
const FOLDER_MIGRATED_KEY = 'timbo_hov_folder_creators_migrated_v1';
const VIDEO_CREATOR_KEY = 'timbo_hov_video_creators';              // legacy "this video only" picks — migrated to hov_status.creator_override once, then removed

const FEATURES  = ['AIBG', 'MODEL', 'ANIM', 'BIS', 'FLATLAY', 'STAGE', 'GHOST'];
const LANGUAGES = ['US', 'ES', 'PT'];
const SSD_BASE  = '/Volumes/INFLUENCE/INF ADS';
const EM_DASH   = '—';

const LAST_SYNC_KEY = 'timbo_hov_last_sync';
const AUTO_SYNC_MS  = 5 * 60 * 1000;
const PAGE_SIZE     = 200;    // rows per GET /api/hov/index page (infinite scroll)
// The export loop (download, write, mark) lives in ./scanExport.js, outside this component, so it survives a tab switch.

// Index row (Supabase hov_index, via GET /api/hov/index) → the file shape the tab already uses.
// `prev` is the same file from the legacy localStorage list (read fallback). The server returns the
// EFFECTIVE creator (hov_status.creator_override first). Unresolved → 'unknown' internally (never stored).
function rowToFile(r, prev, kc, folderMap) {
  var p = prev || {};
  var base = {
    id: r.drive_id,
    originalName: r.name,
    parents: r.folder_id ? [r.folder_id] : [],
    folderName: r.folder_name || null,
    createdTime: r.created_time,
    modifiedTime: r.created_time,
    addedAt: r.added_at,
    size: r.size_label,
    sizeBytes: r.size || 0,
    driveUrl: 'https://drive.google.com/file/d/' + r.drive_id + '/view',
    duration: '?',
    creator: r.creator_display || null,
  };
  var vc = r.creator_override || null;
  var creator = vc ? vc
    : (p.creatorConfirmed && p.creator && p.creator !== 'unknown') ? p.creator
    : resolveCreator(base, kc, folderMap);
  var exportedAt = r.exported_at ? new Date(r.exported_at).getTime() : (p.exportedAt || null);
  var legacyFeatureSet = !!(p.feature && p.featureSuggested === false);
  var f = Object.assign({}, base, {
    creator: creator,
    creatorConfirmed: !!(vc || p.creatorConfirmed),
    creatorOverride: vc,
    videoName: r.video_name != null ? r.video_name : (p.videoName || ''),
    // feature: set by Yann (hov_status) > set in the legacy local list > detected in the filename.
    // No more default guess: a video without a feature goes to the "needs a feature" queue.
    feature: r.feature || (legacyFeatureSet ? p.feature : null) || r.feature_detected || null,
    featureSet: !!r.feature || legacyFeatureSet,
    featureSource: (r.feature || legacyFeatureSet) ? 'set' : (r.feature_detected ? 'detected' : null),
    featureCandidates: r.feature_candidates || null,
    suggestedFeature: r.suggested_feature || null,
    suggestedShare: r.suggested_share || null,
    suggestedBasis: r.suggested_basis || null,
    market: r.language || p.market || r.language_guess,
    exportedAt: exportedAt,
    exportedName: r.exported_name || null,
    status: exportedAt ? 'done' : 'pending',
  });
  f.customName = buildFilename(f);
  f.destPath = buildDest(f);
  return f;
}

// Loaded rows that no longer match the server filter they were fetched with (exported, or tagged
// while on "No feature"). They stay on screen until the next reload, so paging skips them.
function goneFromView(list, status) {
  if (status === 'pending') return list.filter(function(f){ return f.exportedAt; }).length;
  if (status === 'nofeature') return list.filter(function(f){ return f.exportedAt || f.featureSet; }).length;
  return 0;
}

// Thumbnail status: exported → done (green), named or feature chosen → part (blue), else new (orange).
function dotState(f) {
  if (f.exportedAt) return 'done';
  if ((f.videoName && String(f.videoName).trim()) || f.featureSet) return 'part';
  return 'new';
}

// "CadelJenkins" → "Cadel", "Marta Liachovič" → "Marta", "ZRodrix" → "ZRodrix".
function firstName(c) {
  if (!c || c === 'unknown') return '?';
  var s = String(c).trim().split(/[\s_-]+/)[0] || String(c);
  var m = s.match(/^.*?[a-zà-ſ](?=[A-Z])/);
  return m ? m[0] : s;
}

function loadLastSync() { try { return parseInt(localStorage.getItem(LAST_SYNC_KEY) || '0', 10) || 0; } catch { return 0; } }
function saveLastSync() { try { localStorage.setItem(LAST_SYNC_KEY, String(Date.now())); } catch {} }
function loadJSON(key) { try { var r = localStorage.getItem(key); return r ? JSON.parse(r) || {} : {}; } catch { return {}; } }

function apiError(err) {
  return (err && err.response && err.response.data && (err.response.data.details || err.response.data.message || err.response.data.error)) || (err && err.message) || 'Request failed';
}

function loadLS() { try { var r = localStorage.getItem(LS_KEY); return r ? JSON.parse(r) : null; } catch { return null; } }
function loadFolderMap() { return loadJSON(FOLDER_MAP_KEY); }

async function pickDirectory() {
  if (typeof window === 'undefined' || !('showDirectoryPicker' in window)) return null;
  try {
    return await window.showDirectoryPicker({ mode: 'readwrite' });
  } catch (err) {
    if (err.name === 'AbortError') return null;
    return null;
  }
}

function resolveCreator(file, knownCreators, folderMap) {
  var kc = knownCreators || [];
  // 1) raw candidate: backend folder name → learned folder override → whitelist substring.
  var raw = null;
  if (file.creator && file.creator !== 'unknown') raw = file.creator;
  if (!raw) {
    var parents = file.parents || [];
    for (var pi = 0; pi < parents.length; pi++) { if (folderMap[parents[pi]]) { raw = folderMap[parents[pi]]; break; } }
  }
  if (!raw) {
    var nameKey = canonKey(file.originalName || file.id || '');
    for (var i = 0; i < kc.length; i++) { var ck = canonKey(kc[i]); if (ck.length > 3 && nameKey.indexOf(ck) !== -1) { raw = kc[i]; break; } }
  }
  // 2) normalise to ONE canonical display (whitelist form when the same person), else 'unknown'.
  return canonicalCreator(raw, kc) || 'unknown';
}

function slug(s) { return (s || 'unknown').toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, ''); }

// Export filename (before the _NN counter): <creator>_<feature>_<lang>[_<name>], lowercase,
// creator slugified with accents/spaces stripped — "MartaLiachovič" → "martaliachovic".
function slugCreator(c) { return canonKey(c && c !== 'unknown' ? c : 'unknown') || 'unknown'; }
function slugOptName(s) { return canonKey(s); }
function exportPrefix(f, name) {
  var parts = [slugCreator(f.creator), String(f.feature || 'xx').toLowerCase(), String(f.market || 'US').toLowerCase()];
  var n = slugOptName(name); if (n) parts.push(n);
  return parts.join('_');
}
function pad2(n) { return String(n).padStart(2, '0'); }
function buildFilename(f) { return exportPrefix(f, ''); }
function buildDest(f) { return SSD_BASE + '/' + (f.market || 'US') + '/' + slug(f.creator); }

function getISOWeek(date) {
  var d = new Date(date); d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  return { week: Math.ceil((((d - new Date(d.getFullYear(),0,1)) / 86400000) + 1) / 7), year: d.getFullYear() };
}
function weekKey(iso) { if (!iso) return null; var r = getISOWeek(new Date(iso)); return r.year+'-W'+String(r.week).padStart(2,'0'); }
function weekLabel(key) {
  if (!key) return ''; var parts = key.split('-W'); var year = parseInt(parts[0],10); var w = parseInt(parts[1],10);
  var jan4 = new Date(year,0,4); var mon = new Date(jan4); mon.setDate(jan4.getDate()-(jan4.getDay()||7)+1+(w-1)*7);
  var sun = new Date(mon); sun.setDate(mon.getDate()+6);
  var fmt = function(d){ return d.toLocaleDateString('en-US',{month:'short',day:'numeric'}); };
  return 'W'+w+' '+EM_DASH+' '+fmt(mon)+' to '+fmt(sun);
}
// Last `n` ISO weeks, newest first — relative to today, so it keeps working across New Year.
function recentWeeks(n) {
  var weeks = [], seen = {}, d = new Date();
  for (var i = 0; weeks.length < n && i < n * 8; i++) {
    var k = weekKey(d.toISOString());
    if (!seen[k]) { seen[k] = true; weeks.push(k); }
    d.setDate(d.getDate() - 7);
  }
  return weeks;
}
function dayLabel(iso) {
  var d = new Date(iso), t = new Date(), y = new Date(); y.setDate(t.getDate()-1);
  if (d.toDateString()===t.toDateString()) return 'TODAY';
  if (d.toDateString()===y.toDateString()) return 'YESTERDAY';
  return d.toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'}).toUpperCase();
}
function groupByDay(files) {
  var g = {};
  files.forEach(function(f){ var l = dayLabel(f.createdTime||f.modifiedTime||new Date().toISOString()); if (!g[l]) g[l]=[]; g[l].push(f); });
  return g;
}
// Period dropdown → server date range on created_time (since inclusive, until exclusive).
function weekStart(key) {
  var parts = key.split('-W'); var year = parseInt(parts[0], 10); var w = parseInt(parts[1], 10);
  var jan4 = new Date(year, 0, 4); var mon = new Date(jan4);
  mon.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1 + (w - 1) * 7); mon.setHours(0, 0, 0, 0);
  return mon;
}
function periodRange(period) {
  if (!period || period === 'all') return {};
  if (period === 'today') { var t = new Date(); t.setHours(0, 0, 0, 0); return { since: t.toISOString() }; }
  var mon = weekStart(period === 'this-week' ? weekKey(new Date().toISOString()) : period);
  var next = new Date(mon); next.setDate(mon.getDate() + 7);
  return { since: mon.toISOString(), until: next.toISOString() };
}
function agoLabel(ms, now) {
  if (!ms) return null;
  var m = Math.max(0, Math.round((now - ms) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return m + ' min ago';
  var h = Math.round(m / 60);
  if (h < 24) return h + ' h ago';
  return Math.round(h / 24) + ' d ago';
}
function stampLabel(v) {
  var d = v ? new Date(v) : null;
  if (!d || isNaN(d.getTime())) return v || '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function HOVTab({ hovState, setHovState }) {
  var def = { files:[], activeId:null, scanning:false, scanDone:false, scanError:null, period:'all' };
  var whitelist=useState(getCreators)[0];
  var scr=useState([]); var creatorRows=scr[0]; var setCreatorRows=scr[1];
  var s2=useState(false); var showModal=s2[0]; var setShowModal=s2[1];
  var sas=useState(''); var activeSearch=sas[0]; var setActiveSearch=sas[1];  // grid scope: a creator name or a keyword (from the ask bar)
  var sv=useState('all'); var view=sv[0]; var setView=sv[1];                 // all | pending | exported | nofeature
  var sst=useState(null); var stats=sst[0]; var setStats=sst[1];
  var spg=useState({ total:null, loading:false, ms:null }); var page=spg[0]; var setPage=spg[1];
  var sb=useState(new Set()); var batchSel=sb[0]; var setBatchSel=sb[1];
  var sfo=useState(null); var folderOffer=sfo[0]; var setFolderOffer=sfo[1];   // "apply to the whole folder?" after a creator pick
  var sqc=useState(null); var queueCur=sqc[0]; var setQueueCur=sqc[1];       // current row of the "needs a feature" queue
  var sff=useState(null); var focusFeature=sff[0]; var setFocusFeature=sff[1]; // folder row highlighted in the grid
  var sbn=useState(''); var batchName=sbn[0]; var setBatchName=sbn[1];       // optional name for the whole selection
  var exporting = useScanExport();   // the run lives in ./scanExport.js: same state whichever tab is open

  var sy=useState(false); var syncing=sy[0]; var setSyncing=sy[1];
  var sm=useState(''); var syncMsg=sm[0]; var setSyncMsg=sm[1];                // '+45 new' right after a sync that found some
  var sls=useState(loadLastSync); var lastSyncAt=sls[0]; var setLastSyncAt=sls[1];
  var snw=useState(Date.now()); var nowTick=snw[0]; var setNowTick=snw[1];     // re-renders "synced 2 min ago"
  var sfr=useState(false); var fullSync=sfr[0]; var setFullSync=sfr[1];

  var dirHandleRef = useRef(null);
  var syncBusyRef = useRef(false);
  var statusTimers = useRef({});
  var lastClickRef = useRef(null);                         // anchor for shift-click ranges
  var knownRef = useRef({});                               // id → latest file object (selection survives reloads)
  var reqRef = useRef(0);                                  // drops responses of superseded list queries
  var filesRef = useRef([]);
  var pageRef = useRef(page);
  var hasMoreRef = useRef(false);
  var statsTimer = useRef(null);                           // debounced stats refresh after feature tags
  var scrollRef = useRef(null);
  var sentinelRef = useRef(null);
  var exportSelRef = useRef(null);                         // selection left by the last export (its summary clears on the next one)
  var legacyRef = useRef(null);
  if (legacyRef.current === null) {
    var legacy = loadLS(); var lm = {};
    ((legacy && legacy.files) || []).forEach(function(f){ lm[f.id] = f; });
    legacyRef.current = lm;
  }

  var state=hovState||def;
  // Functional update against React's latest state. Nothing is written to localStorage any more:
  // the index is the source of truth and timbo_hov_v4 stays as a read-only fallback.
  var setState=useCallback(function(updater){
    if (!setHovState) return;
    setHovState(function(prev){
      var cur=prev||def;
      return typeof updater==='function'?updater(cur):updater;
    });
  },[setHovState]); // eslint-disable-line
  function update(patch){ setState(function(s){ return Object.assign({},s||def,patch); }); }

  var files=state.files||[];
  filesRef.current = files;
  pageRef.current = page;
  var activeId=state.activeId||null;
  var scanning=state.scanning||false;
  var scanError=state.scanError||null;
  var period=state.period||'all';
  var searching = !!activeSearch;
  var visibleFiles = files;
  var activeFile=files.find(function(f){ return f.id===activeId; })||null;
  var groups=groupByDay(files);
  var allWeeks=recentWeeks(52);
  var goneCount = searching ? 0 : goneFromView(files, view);
  var hasMore = page.total !== null && page.total !== undefined && files.length - goneCount < page.total;
  hasMoreRef.current = hasMore;

  // ── Creators (server: hov_creators) ──────────────────────────────────────
  // Dropdown = active first, then inactive; not_creator hidden. Falls back to the local whitelist
  // when the creators endpoint is unavailable.
  var activeNames = creatorRows.filter(function(c){ return c.status === 'active'; }).map(function(c){ return c.display_name; });
  var inactiveNames = creatorRows.filter(function(c){ return c.status === 'inactive'; }).map(function(c){ return c.display_name; });
  if (!creatorRows.length) activeNames = whitelist.slice();

  function loadCreators() {
    return axios.get(API + '/api/hov/creators', { timeout: 30000 })
      .then(function(r){ setCreatorRows(r.data.creators || []); if (r.data.seeded) console.log('[hov] creators seeded: ' + r.data.seeded); return r.data; })
      .catch(function(e){ console.warn('[hov] creators unavailable', apiError(e)); return null; });
  }
  function loadStats() {
    return axios.get(API + '/api/hov/index/stats', { timeout: 30000 })
      .then(function(r){ setStats(r.data); return r.data; })
      .catch(function(e){ console.warn('[hov] stats unavailable', apiError(e)); return null; });
  }

  // ── List: server-side query + paging (never the whole index in memory) ────
  function searchCreatorMatch(q) {
    var k = canonKey(q); if (!k) return null;
    for (var i = 0; i < creatorRows.length; i++) { if (creatorRows[i].creator_key === k || canonKey(creatorRows[i].display_name) === k) return creatorRows[i]; }
    return null;
  }
  // Search ignores status on purpose: a creator's WHOLE catalogue, exported included.
  function queryParams() {
    if (activeSearch) {
      var m = searchCreatorMatch(activeSearch);
      return m ? { creator: m.creator_key, status: 'all' } : { q: activeSearch, status: 'all' };
    }
    return Object.assign({ status: view, sort: view === 'pending' || view === 'nofeature' ? 'oldest' : 'newest' }, periodRange(period));
  }
  var queryKey = JSON.stringify(queryParams());
  var searchMatch = activeSearch ? searchCreatorMatch(activeSearch) : null;

  function loadPage(reset) {
    var params = queryParams();
    var offset = reset ? 0 : Math.max(0, filesRef.current.length - goneFromView(filesRef.current, params.status));
    var my = ++reqRef.current;
    var t0 = Date.now();
    setPage(function(p){ return Object.assign({}, p, { loading: true }); });
    if (reset && !filesRef.current.length) update({ scanning: true, scanError: null });
    return axios.get(API + '/api/hov/index', { params: Object.assign({}, params, { limit: PAGE_SIZE, offset: offset }), timeout: 30000 })
      .then(function(res){
        if (my !== reqRef.current) return;
        var kc = getCreators(); var fm = loadFolderMap();
        var rows = (res.data.rows || []).map(function(r){ return rowToFile(r, legacyRef.current[r.drive_id], kc, fm); });
        var ms = Date.now() - t0;
        console.log('[hov] list ' + JSON.stringify(params) + ' offset=' + offset + ' rows=' + rows.length + ' total=' + res.data.total + ' in ' + ms + 'ms (server ' + res.data.ms + 'ms)');
        setState(function(s){
          var base = reset ? [] : (s.files || []);
          var seen = {}; base.forEach(function(f){ seen[f.id] = true; });
          var merged = base.concat(rows.filter(function(f){ return !seen[f.id]; }));
          var keep = s.activeId && merged.some(function(f){ return f.id === s.activeId; });
          return Object.assign({}, s, { files: merged, scanning: false, scanDone: true, scanError: null,
            activeId: keep ? s.activeId : (reset ? (merged[0] ? merged[0].id : null) : s.activeId) });
        });
        if (reset && scrollRef.current) scrollRef.current.scrollTop = 0;
        setPage({ total: res.data.total, loading: false, ms: ms });
      })
      .catch(function(err){
        if (my !== reqRef.current) return;
        setPage(function(p){ return Object.assign({}, p, { loading: false }); });
        update({ scanning: false, scanError: 'Index unavailable: ' + apiError(err) });
      });
  }
  function loadMore() {
    var pg = pageRef.current;
    if (pg.loading || !hasMoreRef.current) return;
    loadPage(false);
  }

  useEffect(function(){ loadPage(true); }, [queryKey]); // eslint-disable-line

  // Infinite scroll: load the next page when the sentinel nears the bottom of the left column.
  useEffect(function(){
    var el = sentinelRef.current, root = scrollRef.current;
    if (!el || !root || typeof IntersectionObserver === 'undefined') return;
    var io = new IntersectionObserver(function(entries){ if (entries[0] && entries[0].isIntersecting) loadMore(); }, { root: root, rootMargin: '600px 0px' });
    io.observe(el);
    return function(){ io.disconnect(); };
  }, [queryKey, hasMore, page.loading]); // eslint-disable-line

  // Mount: counts + creators; first build if the index is empty, background sync if stale;
  // then the one-shot migration of the legacy folder → creator map.
  useEffect(function(){
    Promise.all([loadStats(), loadCreators()]).then(function(res){
      var st = res[0];
      if (st && st.total === 0) doSync();
      else if (st && Date.now() - loadLastSync() > AUTO_SYNC_MS) doSync();
      migrateFolderMapOnce();
      migrateVideoCreatorsOnce();
    });
  }, []); // eslint-disable-line

  // One-shot: legacy localStorage "this video only" picks → hov_status.creator_override, then the
  // key is removed (kept if any write fails, retried next open).
  function migrateVideoCreatorsOnce() {
    var map = loadJSON(VIDEO_CREATOR_KEY); var ids = Object.keys(map);
    if (!ids.length) return;
    Promise.all(ids.map(function(id){ return postStatus(id, { creator: map[id] }); })).then(function(res){
      var failed = res.filter(function(ok){ return !ok; }).length;
      console.log('[hov] video creator picks migrated: ' + (ids.length - failed) + '/' + ids.length);
      if (!failed) { try { localStorage.removeItem(VIDEO_CREATOR_KEY); } catch {} loadPage(true); }
    });
  }

  function migrateFolderMapOnce() {
    var done = false; try { done = !!localStorage.getItem(FOLDER_MIGRATED_KEY); } catch {}
    if (done) return;
    var map = loadFolderMap();
    if (!Object.keys(map).length) { try { localStorage.setItem(FOLDER_MIGRATED_KEY, new Date().toISOString()); } catch {} return; }
    axios.post(API + '/api/hov/folder-creators/import', { map: map }, { timeout: 120000 })
      .then(function(r){
        console.log('[hov] folder → creator map migrated', r.data);
        try { localStorage.setItem(FOLDER_MIGRATED_KEY, new Date().toISOString()); } catch {}
        if (r.data && r.data.imported) { loadPage(true); loadCreators(); }
      })
      .catch(function(e){ console.warn('[hov] folder map migration failed (will retry next open)', apiError(e)); });
  }

  // Sync = incremental Drive → index sync on the backend (automatic on open), then re-read list,
  // counts, creators. full=true re-lists the whole Drive: the only pass that catches videos moved
  // or deleted in Drive, which the incremental sync misses by design.
  function doSync(full) {
    if (syncBusyRef.current) return Promise.resolve();
    syncBusyRef.current = true; setSyncing(true); setSyncMsg(''); setFullSync(!!full);
    if (!filesRef.current.length) update({ scanning: true, scanError: null });
    return axios.post(API + '/api/hov/index/sync' + (full ? '?full=1' : ''), { creators: getCreators() }, { timeout: 600000 })
      .then(function(res){
        var d = res.data || {};
        console.log('[hov] sync', d);
        saveLastSync(); setLastSyncAt(Date.now()); setNowTick(Date.now());
        setSyncMsg(d.added > 0 ? '+' + d.added + ' new' : '');
        return Promise.all([loadStats(), loadCreators()]).then(function(){
          if (d.added > 0 || !filesRef.current.length) return loadPage(true);
          // The first page may have landed while the sync set "Loading index…": clear it.
          update({ scanning: false });
        });
      })
      .catch(function(err){ update({ scanning: false, scanError: 'Sync failed: ' + apiError(err) }); })
      .then(function(){ syncBusyRef.current = false; setSyncing(false); setFullSync(false); });
  }
  useEffect(function(){
    var t = setInterval(function(){ setNowTick(Date.now()); }, 30000);
    return function(){ clearInterval(t); };
  }, []); // eslint-disable-line

  // ── Status writes (hov_status) ───────────────────────────────────────────
  // Persist video name / feature / language to the index (debounced per video, typing-safe).
  function postStatus(id, body) {
    return axios.post(API + '/api/hov/status/' + encodeURIComponent(id), body, { timeout: 15000 })
      .then(function(){ return true; })
      .catch(function(e){ console.warn('[hov] status write failed', id, apiError(e)); return false; });
  }
  function persistStatus(id, patch) {
    var body = {};
    if ('videoName' in patch) body.video_name = patch.videoName || null;
    if ('feature' in patch) body.feature = patch.feature || null;
    if ('market' in patch) body.language = patch.market || null;
    if (!Object.keys(body).length) return;
    var t = statusTimers.current; var pend = t[id] || { body: {} };
    clearTimeout(pend.timer);
    pend.body = Object.assign({}, pend.body, body);
    pend.timer = setTimeout(function(){
      delete t[id];
      var tagged = 'feature' in pend.body;
      postStatus(id, pend.body).then(function(){
        if (!tagged) return;
        clearTimeout(statsTimer.current);
        statsTimer.current = setTimeout(loadStats, 1500);
      });
    }, 600);
    t[id] = pend;
  }

  function updateFile(id,patch){
    persistStatus(id, patch);
    updateFileLocal(id, patch);
  }
  // Local-only patch (no status write): state + the selection cache.
  function updateFileLocal(id, patch){
    if ('feature' in patch && !('featureSet' in patch)) patch = Object.assign({}, patch, { featureSet: true });
    if (knownRef.current[id]) knownRef.current[id] = Object.assign({}, knownRef.current[id], patch);
    setState(function(s){ return Object.assign({},s||def,{files:(s.files||[]).map(function(f){ return f.id===id?Object.assign({},f,patch):f; })}); });
  }

  // ── Creator corrections (server: hov_folder_creators) ────────────────────
  function withCreator(f, creator) {
    var u = Object.assign({}, f, { creator: creator, creatorConfirmed: creator !== 'unknown' });
    u.customName = buildFilename(u); u.destPath = buildDest(u);
    return u;
  }
  function applyCreatorLocal(pred, creator) {
    Object.keys(knownRef.current).forEach(function(id){ var f = knownRef.current[id]; if (pred(f)) knownRef.current[id] = withCreator(f, creator); });
    setState(function(s){ return Object.assign({}, s, { files: (s.files || []).map(function(f){ return pred(f) ? withCreator(f, creator) : f; }) }); });
  }
  // "This video only" pick → hov_status.creator_override (server). Clearing only calls the server
  // when the video actually has an override.
  function rememberVideoCreator(id, creator) {
    if (!creator) {
      var f = filesRef.current.find(function(x){ return x.id === id; });
      if (!f || !f.creatorOverride) return Promise.resolve(true);
    }
    setState(function(st){ return Object.assign({}, st, { files: (st.files || []).map(function(x){ return x.id === id ? Object.assign({}, x, { creatorOverride: creator || null }) : x; }) }); });
    return postStatus(id, { creator: creator || null });
  }
  // A pick in the rail applies to that video only (hov_status.creator_override). When its folder holds
  // other videos, the rail then offers to apply it to the whole folder; ignored, nothing else happens.
  async function correctCreator(file, creator) {
    if (!file || !creator) return;
    applyCreatorLocal(function(f){ return f.id === file.id; }, creator);
    rememberVideoCreator(file.id, creator);
    setFolderOffer(null);
    var folderId = (file.parents || [])[0];
    if (!folderId) return;
    try {
      var r = await axios.get(API + '/api/hov/index', { params: { folder: folderId, status: 'all', limit: 1 }, timeout: 15000 });
      var n = r.data.total;
      if (n > 1) setFolderOffer({ fileId: file.id, folderId: folderId, folderName: file.folderName || null, creator: creator, n: n });
    } catch (e) { console.warn('[hov] folder size unavailable', apiError(e)); }
  }
  // Offer accepted: the folder correction (saved server-side, re-resolves the folder's rows and
  // future syncs). The video pick is then redundant and cleared.
  async function applyFolderOffer(offer) {
    if (!offer) return;
    setFolderOffer(Object.assign({}, offer, { busy: true, error: null }));
    try {
      var res = await axios.post(API + '/api/hov/folder-creators', { folder_id: offer.folderId, creator: offer.creator }, { timeout: 30000 });
      var disp = (res.data && res.data.creator_display) || offer.creator;
      rememberVideoCreator(offer.fileId, null);
      applyCreatorLocal(function(f){ return (f.parents || [])[0] === offer.folderId; }, disp);
      setFolderOffer(null);
      loadCreators();
    } catch (err) {
      var msg = err.response && err.response.status === 422 ? apiError(err) : 'Could not save the folder (' + apiError(err) + ')';
      console.warn('[hov] folder correction failed — kept for this video only', apiError(err));
      setFolderOffer(Object.assign({}, offer, { busy: false, error: msg + '. Kept for this video only.' }));
    }
  }

  // ── Selection = the unit of work ─────────────────────────────────────────
  // Selected ids live in batchSel; knownRef keeps the latest copy of every file seen, so a
  // selection survives a chip / search change that unloads its thumbnails.
  files.forEach(function(f){ knownRef.current[f.id] = f; });
  var selectedFiles = [];
  batchSel.forEach(function(id){ var f = knownRef.current[id]; if (f) selectedFiles.push(f); });
  var queue = selectedFiles.filter(function(f){ return !f.feature; });           // "Needs a feature"
  var queueTarget = queue.find(function(f){ return f.id === queueCur; }) || queue[0] || null;
  var unresolvedSel = selectedFiles.filter(function(f){ return !f.creator || f.creator === 'unknown'; });
  var folderGroups = {};
  selectedFiles.forEach(function(f){ if (f.feature) (folderGroups[f.feature] = folderGroups[f.feature] || []).push(f); });
  var folderRows = Object.keys(folderGroups).map(function(k){ return [k, folderGroups[k]]; })
    .sort(function(a, b){ return b[1].length - a[1].length; });

  function setSelection(next) { setBatchSel(next); }
  // "7 / 7 exported" stays until the selection changes, then clears.
  useEffect(function(){
    if (exportSelRef.current && batchSel !== exportSelRef.current) {
      exportSelRef.current = null;
      clearSummary();
    }
  }, [batchSel]); // eslint-disable-line
  function toggleBatchSel(id) {
    setBatchSel(function(prev){
      var next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }
  function clearBatch() { setSelection(new Set()); setQueueCur(null); setFocusFeature(null); }

  // Click toggles, shift-click selects the range from the last click (in grid order).
  function onThumbClick(e, f) {
    var last = lastClickRef.current;
    if (e && e.shiftKey && last && last !== f.id) {
      var flat = filesRef.current;
      var a = flat.findIndex(function(x){ return x.id === last; });
      var b = flat.findIndex(function(x){ return x.id === f.id; });
      if (a !== -1 && b !== -1) {
        var lo = Math.min(a, b), hi = Math.max(a, b);
        setBatchSel(function(prev){ var next = new Set(prev); for (var i = lo; i <= hi; i++) next.add(flat[i].id); return next; });
      } else toggleBatchSel(f.id);
    } else toggleBatchSel(f.id);
    lastClickRef.current = f.id;
    update({ activeId: f.id });
  }
  // "select all" on a day group — toggles: all selected → unselect them.
  function toggleDay(gFiles) {
    setBatchSel(function(prev){
      var next = new Set(prev);
      var all = gFiles.every(function(f){ return next.has(f.id); });
      gFiles.forEach(function(f){ if (all) next.delete(f.id); else next.add(f.id); });
      return next;
    });
  }

  // ── Feature tagging — number keys never overwrite silently ───────────────
  function setFeature(id, feat) {
    updateFile(id, { feature: feat, featureSet: true, featureSource: 'set' });
  }
  // 1–7: tag the CURRENT row of the queue, then move to the next row. Nothing already tagged is touched.
  function tagQueueCurrent(feat) {
    var target = queueTarget;
    if (!target) return;
    setFeature(target.id, feat);
    var rest = queue.filter(function(f){ return f.id !== target.id; });
    var idx = queue.findIndex(function(f){ return f.id === target.id; });
    var next = rest.length ? (rest[idx] || rest[0]) : null;
    setQueueCur(next ? next.id : null);
    if (next) update({ activeId: next.id });
  }
  // shift + 1–7: retag the WHOLE selection, overwriting — the explicit gesture.
  function retagSelection(feat) {
    selectedFiles.forEach(function(f){ if (f.feature !== feat || f.featureSource !== 'set') setFeature(f.id, feat); });
  }
  function pressFeature(feat, shift) {
    if (!selectedFiles.length) return;
    if (shift) retagSelection(feat); else tagQueueCurrent(feat);
  }
  // Language follows the same rule: click = current video, shift-click = whole selection.
  function pressLanguage(lang, shift, current) {
    var targets = shift ? selectedFiles : (current ? [current] : []);
    targets.forEach(function(f){ if (f.market !== lang) updateFile(f.id, { market: lang }); });
  }

  // Grid keyboard (ignored while an input has focus): arrows move the cursor, Space toggles it,
  // 1–7 tags the queue's current row, shift+1–7 retags the selection, Enter exports, Esc clears.
  useEffect(function(){
    function onKeyDown(e){
      var tgt = e.target || {};
      var typing = /^(INPUT|TEXTAREA|SELECT)$/.test(tgt.tagName || '') || tgt.isContentEditable;
      if (typing) { if (e.key === 'Escape' && tgt.blur) tgt.blur(); return; }
      if (showModal || exporting.running) return;
      var digit = /^Digit([1-7])$/.exec(e.code || '') || (/^[1-7]$/.test(e.key) ? [null, e.key] : null);
      if (digit && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); pressFeature(FEATURES[parseInt(digit[1], 10) - 1], e.shiftKey); return; }
      if (e.key === 'Escape') { clearBatch(); return; }
      if (e.key === 'Enter') { e.preventDefault(); if (canExport) exportSelection(); return; }
      var flat = visibleFiles;
      if (!flat.length) return;
      var idx = flat.findIndex(function(f){ return f.id === activeId; });
      if (e.key === ' ') { e.preventDefault(); var c = flat[idx] || flat[0]; if (c) { toggleBatchSel(c.id); lastClickRef.current = c.id; update({ activeId: c.id }); } }
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); var n = flat[Math.min((idx < 0 ? -1 : idx) + 1, flat.length - 1)]; if (n) update({ activeId: n.id }); if (idx >= flat.length - 3) loadMore(); }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); var p = flat[Math.max((idx < 0 ? 0 : idx) - 1, 0)]; if (p) update({ activeId: p.id }); }
    }
    document.addEventListener('keydown', onKeyDown);
    return function(){ document.removeEventListener('keydown', onKeyDown); };
  }); // re-bound every render: the handlers read the current selection / queue

  async function ensureDirHandle() {
    if (dirHandleRef.current) {
      try {
        var perm = await dirHandleRef.current.queryPermission({ mode: 'readwrite' });
        if (perm === 'granted') return dirHandleRef.current;
        var req = await dirHandleRef.current.requestPermission({ mode: 'readwrite' });
        if (req === 'granted') return dirHandleRef.current;
      } catch(e) { console.warn('[hov] dir handle perm check failed', e); }
      dirHandleRef.current = null;
    }
    var handle = await pickDirectory();
    if (handle) dirHandleRef.current = handle;
    return handle;
  }

  // ── Export: one sub-folder per feature, one directory pick, mark each video as it lands ──
  var canExport = selectedFiles.length > 0 && queue.length === 0 && unresolvedSel.length === 0 && !exporting.running;
  async function exportSelection() {
    var batch = selectedFiles.slice();
    if (!batch.length || batch.some(function(f){ return !f.feature; })) return;
    if (!claimScanExport()) return;   // one export at a time, also when you come back to this tab mid-run
    var hasFS = typeof window !== 'undefined' && 'showDirectoryPicker' in window;
    var root = null;
    if (hasFS) { root = await ensureDirHandle(); if (!root) { releaseScanExport(); return; } }
    var name = (batchName || '').trim();
    batch.sort(function(a, b){ return a.feature.localeCompare(b.feature) || String(a.createdTime).localeCompare(String(b.createdTime)); });
    var prefixes = {};
    batch.forEach(function(f){ prefixes[exportPrefix(f, name)] = true; });
    var counters = {};
    try {
      var cr = await axios.post(API + '/api/hov/export/counters', { prefixes: Object.keys(prefixes) }, { timeout: 30000 });
      counters = (cr.data && cr.data.counters) || {};
    } catch (e) {
      if (!window.confirm('Could not read the existing numbering (' + apiError(e) + ').\nExport anyway? Numbers may repeat earlier exports.')) { releaseScanExport(); return; }
    }
    // From here the loop belongs to the session, not to this component.
    startScanExport({ api: API, batch: batch, root: root, counters: counters, prefixOf: function(f){ return exportPrefix(f, name); } });
  }

  // Whatever the run did while this tab was away (or open) is applied here: files that landed are
  // marked exported locally; when the run has ended, the selection keeps only what can be retried.
  var appliedRef = useRef(0);
  useEffect(function(){
    var list = getLanded();
    if (list.length < appliedRef.current) appliedRef.current = 0;          // a new run started
    for (var i = appliedRef.current; i < list.length; i++) updateFileLocal(list[i].id, { exportedAt: list[i].exportedAt, exportedName: list[i].exportedName, status: 'done' });
    appliedRef.current = list.length;
    if (!exporting.running) {
      var out = takeOutcome();
      if (out) { exportSelRef.current = out.failedIds; setSelection(out.failedIds); setQueueCur(null); setFocusFeature(null); loadStats(); }
    }
  }, [exporting]); // eslint-disable-line
  // Tell the global dock / RenderBar this tab already shows the export.
  useEffect(function(){ return registerPresenter(); }, []);

  // "Re-sync this video" (export said not found): Drive is read again for this one file. Moved →
  // its folder / creator are refreshed and it stays selected for "Retry failed". Gone → dropped
  // from the selection, with the reason kept in the failure list.
  function patchFailed(id, patch) {
    patchExportFailed(id, patch);
  }
  async function resyncVideo(id) {
    patchFailed(id, { resync: 'running' });
    try {
      var r = await axios.post(API + '/api/hov/index/video/' + encodeURIComponent(id) + '/resync', {}, { timeout: 60000 });
      var d = r.data || {};
      if (d.gone) {
        patchFailed(id, { resync: 'gone', error: d.reason || 'No longer in Drive' });
        setBatchSel(function(prev){ var next = new Set(prev); next.delete(id); exportSelRef.current = next; return next; });
        return;
      }
      var v = d.video || {};
      var cur = knownRef.current[id] || {};
      updateFileLocal(id, { originalName: v.name || cur.originalName, parents: v.folder_id ? [v.folder_id] : cur.parents, folderName: v.folder_name || cur.folderName,
        creator: cur.creatorOverride || v.creator_display || cur.creator });
      patchFailed(id, { resync: 'found', error: d.moved ? 'Found again in “' + (v.folder_name || 'another folder') + '” — retry to export it' : 'Still in Drive — retry to export it' });
    } catch (e) {
      patchFailed(id, { resync: 'failed', error: 'Re-sync failed: ' + apiError(e) });
    }
  }

  // Scope the grid from the ask bar: a creator (whole catalogue) or a filename keyword.
  // Both ignore status on purpose — exported videos stay visible.
  function scopeCreator(c) {
    if (!c) return;
    setActiveSearch(c.display_name || c.creator_key); update({ activeId: null });
  }
  function scopeKeyword(t) {
    var v = String(t || '').trim(); if (v.length < 2) return;
    setActiveSearch(v); update({ activeId: null });
  }
  function clearSearch() { setActiveSearch(''); update({ activeId: null }); }

  var weekOptions=allWeeks.map(function(w){ return {value:w,label:weekLabel(w)}; });
  var pendingCount = stats ? stats.pending_total : null;
  var allCount = stats ? stats.total : null;
  var exportedCount = stats ? Math.max(0, stats.total - stats.pending_total) : null;
  var chipCount = function(n){ return n === null || n === undefined ? EM_DASH : n.toLocaleString('en-US'); };
  var searchLabel = searchMatch ? searchMatch.display_name : '“' + activeSearch + '”';
  var noFeatureCount = stats && stats.no_feature_total !== undefined ? stats.no_feature_total : null;
  var CHIPS = [['all', 'All', allCount], ['pending', 'To process', pendingCount], ['exported', 'Exported', exportedCount], ['nofeature', 'No feature', noFeatureCount]];
  var shownNeedFeature = files.filter(function(f){ return !f.feature && !f.exportedAt; }).length;
  var syncedAgo = agoLabel(lastSyncAt, nowTick);
  var syncStatus = syncing ? (fullSync ? 'Full rescan running…' : 'Syncing…')
    : syncMsg ? syncMsg + (syncedAgo ? ' · synced ' + syncedAgo : '')
    : 'Up to date' + (syncedAgo ? ' · synced ' + syncedAgo : '');

  return (
    <div className="hov-root tb-page">
      <AskScan creators={creatorRows} onScope={scopeCreator} onSearch={scopeKeyword} />
      {scanError&&<div className="hov-error">{scanError}</div>}

      <div className="hov-body">

        {/* LIBRARY — toolbar + grid */}
        <div className="hov-filelist tb-card">
          <PerfBlockHeader icon="▦" title="Library" actions={
            <span className={'tb-sync hov-syncline' + (syncing ? ' busy' : '') + (scanError ? ' err' : '')}>
              <i className="tb-sync-dot" />
              <span className={'hov-sync' + (syncMsg && !syncing ? ' new' : '')}>{syncStatus}</span>
              {!syncing && (
                <button className="hov-fullscan" onClick={function(){ doSync(true); }}
                        title="New videos are picked up automatically when the tab opens. A full rescan re-reads the whole Drive: the only way to catch videos moved or deleted in Drive, which the automatic sync misses by design. Takes a minute or two.">
                  Full rescan
                </button>
              )}
            </span>
          } />
          <div className="hov-toolbar">
            {/* left: the period, or the creator scope · right: the four filters, each with its count */}
            {!searching&&(
              <select className="hov-date-select" value={period} onChange={function(e){ update({period:e.target.value,activeId:null}); }}>
                <option value="all">All dates</option>
                <option value="this-week">This week</option>
                <option value="today">Today</option>
                {weekOptions.map(function(o){ return <option key={o.value} value={o.value}>{o.label}</option>; })}
              </select>
            )}
            {searching && (
              <span className="hov-scope">
                {searchLabel} · all {page.total === null || page.total === undefined ? EM_DASH : page.total.toLocaleString('en-US')} videos, exported included
                <button onClick={clearSearch} title="Back to the full grid" aria-label="Remove scope">&#10005;</button>
              </span>
            )}
            <div className={'hov-seg' + (searching ? ' disabled' : '')}>
              {CHIPS.map(function(c){
                return (
                  <button key={c[0]} className={view === c[0] && !searching ? 'on' : ''} disabled={searching}
                          title={searching ? 'A scoped grid shows every video, whatever its status' : c[0] === 'nofeature' ? 'Not exported, and no feature set or found in the filename' : undefined}
                          onClick={function(){ if (view !== c[0]) { setView(c[0]); update({ activeId: null }); } }}>
                    {c[1]} <b>{chipCount(c[2])}</b>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="hov-filelist-scroll" ref={scrollRef}>
            {scanning&&<div className="hov-list-msg"><span>&#9679;</span>&nbsp;{syncing?'Indexing Drive...':'Loading index...'}</div>}
            {!scanning&&!page.loading&&files.length===0&&(
              <div className="hov-list-msg" style={{flexDirection:'column',gap:8}}>
                <div style={{fontSize:'1.6rem'}}>{!searching && (view === 'pending' || view === 'nofeature') ? '✓' : '📂'}</div>
                <div style={{fontWeight:600,fontSize:12}}>{searching ? 'No results' : view === 'pending' || view === 'nofeature' ? 'All caught up' : 'No videos'}</div>
                <div style={{fontSize:11,color:'var(--text-dim)',textAlign:'center',lineHeight:1.5}}>
                  {searching ? 'Try a different creator or keyword' : view === 'pending' ? 'Nothing left to process for this period. Try the All chip.' : view === 'nofeature' ? 'Every video left to process has a feature. Try the To process chip.' : 'New Drive videos are indexed automatically when this tab opens.'}
                </div>
              </div>
            )}
            {!scanning&&files.length>0&&Object.entries(groups).map(function(entry){
              var lbl=entry[0]; var gFiles=entry[1];
              var gTodo=gFiles.filter(function(f){ return !f.feature && !f.exportedAt; }).length;
              return (
                <div key={lbl}>
                  <div className="hov-day-header"><span className="hov-day-lbl">{lbl}</span>
                    <span className="hov-day-count">{gFiles.length}</span>
                    {gTodo > 0 && <span className="hov-day-todo">{gTodo} {gTodo === 1 ? 'needs' : 'need'} a feature</span>}
                    <button className="hov-day-pick" onClick={function(){ toggleDay(gFiles); }}>{gFiles.every(function(f){ return batchSel.has(f.id); }) ? 'unselect' : 'select all'}</button></div>
                  <div className="hov-thumb-grid">
                    {gFiles.map(function(f){
                      var isSel=batchSel.has(f.id);
                      return (<ThumbCardMemo key={f.id} file={f}
                        active={activeId===f.id}
                        selected={isSel}
                        dim={!!focusFeature && !(isSel && f.feature === focusFeature)}
                        onClick={function(e){ onThumbClick(e, f); }}
                      />);
                    })}
                  </div>
                </div>
              );
            })}
            <div ref={sentinelRef} className="hov-list-more">
              {page.loading && files.length > 0 && <span>Loading more…</span>}
              {!page.loading && hasMore && files.length > 0 && (
                <button className="hov-more-btn" onClick={loadMore}>Load more ({(page.total - files.length + goneCount).toLocaleString('en-US')} left)</button>
              )}
            </div>
          </div>
        </div>

        {/* EXPORT — one L-shaped card: the video, a rail for the current video on its right, and a
            full-width band underneath for the queue, the folders and the export. Sticky. */}
        <div className="hov-work">
          <div className="hov-export tb-card">
            <PerfBlockHeader icon="⤓" title="Export" actions={selectedFiles.length
              ? <span className="hov-sel-head"><b>{selectedFiles.length}</b> selected
                  <button className="hov-batch-clear" onClick={clearBatch} disabled={exporting.running}>clear</button></span>
              : <span className="hov-sel-head">nothing selected</span>} />
            <ExportCard selected={selectedFiles} queue={queue} queueTarget={queueTarget}
              current={activeFile || knownRef.current[activeId] || null}
              folderRows={folderRows} unresolved={unresolvedSel.length}
              shownCount={files.length} shownNeedFeature={shownNeedFeature}
              activeNames={activeNames} inactiveNames={inactiveNames}
              focusFeature={focusFeature} onFocusFeature={setFocusFeature}
              batchName={batchName} onBatchName={setBatchName}
              exporting={exporting} canExport={canExport} onExport={exportSelection}
              onPickQueue={function(f){ setQueueCur(f.id); update({ activeId: f.id }); }}
              onPreview={function(f){ update({ activeId: f.id }); }}
              onFeature={pressFeature}
              onLanguage={pressLanguage}
              onSetCreator={function(f, c){ correctCreator(f, c); }}
              folderOffer={folderOffer} onApplyFolder={applyFolderOffer}
              onDismissFolder={function(){ setFolderOffer(null); }}
              onShowCreatorModal={function(){ setShowModal(true); }}
              onRetryFailed={exportSelection} onResync={resyncVideo} />
          </div>
        </div>
      </div>

      {showModal&&(
        <CreatorManager rows={creatorRows} onClose={function(){ setShowModal(false); }}
          onChanged={function(){ loadCreators(); loadPage(true); }} />
      )}
    </div>
  );
}

// ── ExportCard — the video, its rail (creator · feature · language · filename) and the band
// (needs-a-feature queue · folders · export). The rail edits the video in the player; the
// feature keys follow the queue rules (1–7 the highlighted row, shift = whole selection).
function ExportCard(props) {
  var sel = props.selected, target = props.queueTarget;
  var n = sel.length, ex = props.exporting;
  var name = (props.batchName || '').trim();
  var cur = props.current || target || sel[0] || null;
  return (
    <div className="hov-L">
      <div className="hov-L-video">
        {cur ? <PlayerPanel file={cur}/>
          : <div className="hov-player hov-player-empty"><div className="hov-empty">&#9654;<div className="hov-empty-label">{props.shownCount === 0 ? 'Nothing to show' : 'Select a video'}</div></div></div>}
      </div>
      <div className="hov-L-rail">
        {cur ? <ExportRail {...props} cur={cur} name={name}/> : <div className="hov-rail-empty">Click a thumbnail to see it here.</div>}
      </div>
      <div className="hov-L-band">
        {n === 0 ? (
          <div className="hov-band-empty">
            {ex.msg && !ex.running && <div className="hov-export-msg hov-export-done">{ex.msg}</div>}
            {ex.running && <ExportRunBlock ex={ex} lead={<div className="hov-export-msg"><b>Exporting {ex.done} / {ex.total}…</b> (keeps running if you leave this tab)</div>}/>}
            {props.shownCount > 0 && <span><b>{props.shownNeedFeature.toLocaleString('en-US')}</b> of the {props.shownCount.toLocaleString('en-US')} shown {props.shownNeedFeature === 1 ? 'still needs' : 'still need'} a feature — click a thumbnail to select, shift-click for a range, or “select all” on a day.</span>}
          </div>
        ) : <ExportBand {...props} name={name}/>}
        {!ex.running && ex.failed && ex.failed.length > 0 && (
          <ExportFailures failed={ex.failed} canRetry={props.canExport} onRetry={props.onRetryFailed} onResync={props.onResync} />
        )}
      </div>
    </div>
  );
}

function ExportRail(props) {
  var sel = props.selected, queue = props.queue, target = props.queueTarget, cur = props.cur;
  var n = sel.length, ex = props.exporting;
  var act = (props.activeNames || []).slice().sort(function(a, b){ return a.localeCompare(b); });
  var inact = (props.inactiveNames || []).slice().sort(function(a, b){ return a.localeCompare(b); });
  var curUnknown = !cur.creator || cur.creator === 'unknown';
  var listed = act.concat(inact).find(function(c){ return canonKey(c) === canonKey(cur.creator); });
  var selValue = curUnknown ? '' : (listed || cur.creator);
  var offer = props.folderOffer && props.folderOffer.fileId === cur.id ? props.folderOffer : null;
  var targetIdx = target ? queue.findIndex(function(f){ return f.id === target.id; }) + 1 : 0;
  var preview = exportPrefix(cur.feature ? cur : Object.assign({}, cur, { feature: 'FEATURE' }), props.name) + '_' + pad2(1) + '.mp4';
  return (
    <>
      <div className="hov-rs">
        <div className="hov-rl">Creator</div>
        <div className="hov-creator-row">
          <select className="hov-creator-select" value={selValue} disabled={ex.running} onChange={function(e){ if (e.target.value) props.onSetCreator(cur, e.target.value); }}>
            <option value="">{EM_DASH + ' Select creator'}</option>
            {(!curUnknown && !listed) && <option value={cur.creator}>{cur.creator}</option>}
            {act.map(function(c){ return <option key={c} value={c}>{c}</option>; })}
            {inact.length > 0 && <option disabled value="__sep">{'──── Inactive ────'}</option>}
            {inact.map(function(c){ return <option key={'i-' + c} value={c}>{c}</option>; })}
          </select>
          <button className="hov-gear-btn" onClick={props.onShowCreatorModal} title="Manage creators: merge, rename, add">&#9881;</button>
        </div>
        {offer && (
          <div className="hov-folder-offer">
            <span>Apply to all <b>{offer.n}</b> videos in this folder{offer.folderName ? <span className="dim"> ({offer.folderName})</span> : null}?</span>
            <span className="hov-folder-offer-btns">
              <button className="hov-folder-offer-yes" disabled={offer.busy} onClick={function(){ props.onApplyFolder(offer); }}>{offer.busy ? 'Applying…' : 'Apply to folder'}</button>
              <button className="hov-folder-offer-no" disabled={offer.busy} onClick={props.onDismissFolder} title="Keep it for this video only">This video only</button>
            </span>
            {offer.error && <span className="hov-folder-offer-err">{offer.error}</span>}
          </div>
        )}
      </div>
      <div className="hov-rs">
        <div className="hov-rl">Feature{cur.featureSource === 'detected' ? <span className="hov-rl-note"> · {cur.feature} from the filename</span> : null}</div>
        <div className="hov-batch-feats">
          {FEATURES.map(function(f, i){
            var on = queue.length ? (target && target.feature === f) : (n ? sel.every(function(x){ return x.feature === f; }) : cur.feature === f);
            return <button key={f} className={'hov-opt' + (on ? ' on' : '')} disabled={ex.running || !n}
                           title={n ? undefined : 'Select videos to tag them'}
                           onClick={function(e){ props.onFeature(f, e.shiftKey); }}>{f}<kbd>{i + 1}</kbd></button>;
          })}
        </div>
        <div className="hov-applyline">
          {!n ? <span>Select videos to tag them.</span>
            : queue.length
            ? <span>Next key tags <b>{target ? (target.creator || EM_DASH) : EM_DASH}</b> (row {targetIdx} of {queue.length}), then moves on. <span className="dim">Shift + key retags all {n}.</span></span>
            : <span>Everything is tagged. <span className="dim">Shift + key retags all {n}, overwriting.</span></span>}
        </div>
      </div>
      <div className="hov-rs">
        <div className="hov-rl">Language</div>
        <div className="hov-langs">
          {LANGUAGES.map(function(l){ return <button key={l} className={'hov-opt' + (cur.market === l ? ' on' : '')} title="Shift-click: whole selection" disabled={ex.running}
            onClick={function(e){ props.onLanguage(l, e.shiftKey, cur); }}>{l}</button>; })}
        </div>
      </div>
      <div className="hov-rs">
        <div className="hov-rl">Filename</div>
        <div className="hov-fname">{preview}</div>
        <details className="hov-name-opt">
          <summary>Add a name to the filenames</summary>
          <input className="hov-triage-input" placeholder="e.g. ramen unboxing" value={props.batchName}
                 onChange={function(e){ props.onBatchName(e.target.value); }} />
          <div className="hov-name-hint">Inserted before the counter, for the whole selection.</div>
        </details>
      </div>
    </>
  );
}

// Progress of the running export (Scan's session-level run): the same block with a selection (ExportBand) and without (empty band).
function ExportRunBlock(props) {
  var ex = props.ex;
  return (
    <div className="hov-export-run">
      {props.lead}
      <div className="hov-export-prog"><div style={{width: (ex.total ? Math.round(ex.done / ex.total * 100) : 0) + '%'}}/></div>
      {ex.msg && <div className="hov-export-msg">{ex.msg}</div>}
    </div>
  );
}

function ExportBand(props) {
  var sel = props.selected, queue = props.queue, target = props.queueTarget;
  var n = sel.length, ex = props.exporting, rows = props.folderRows;
  var exportLabel = ex.running ? 'Exporting ' + ex.done + ' / ' + ex.total + '…'
    : queue.length ? queue.length + ' to tag'
    : props.unresolved ? props.unresolved + ' need' + (props.unresolved === 1 ? 's' : '') + ' a creator'
    : 'Export ' + n + ' → ' + rows.length + ' folder' + (rows.length === 1 ? '' : 's');
  return (
    <>
      {queue.length > 0 && (
        <div className="hov-band-sec">
          <div className="hov-qhead"><span className="k bad">Needs a feature</span><span className="c">{queue.length}</span>
            <span className="hint"><kbd>1</kbd>–<kbd>7</kbd> tags the highlighted one, then moves on</span></div>
          <div className="hov-qstrip">
            {queue.map(function(f){
              var sug = f.suggestedFeature || (f.featureCandidates && f.featureCandidates.join('/')) || null;
              return (
                <div key={f.id} className={'hov-qi' + (target && target.id === f.id ? ' cur' : '')}
                     onClick={function(){ props.onPickQueue(f); }} onMouseEnter={function(){ props.onPreview(f); }}
                     title={sug && f.suggestedShare ? sug + ': ' + Math.round(f.suggestedShare * 100) + '% of this creator’s ' + f.suggestedBasis + ' tagged videos' : String(f.originalName || '')}>
                  <span className="hov-qi-th" style={{backgroundImage:'url(' + API + '/api/hov/thumb/' + f.id + ')'}}/>
                  <span className="hov-qi-m"><span className="c1">{f.creator && f.creator !== 'unknown' ? f.creator : 'No creator'}</span>
                    <span className="c2">{sug ? sug + '?' : dayLabel(f.createdTime || new Date().toISOString()).toLowerCase()}</span></span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="hov-band-sec">
        <div className="hov-qhead"><span className="k ok">Ready</span>
          <span className="c">{n - queue.length} in {rows.length} folder{rows.length === 1 ? '' : 's'}</span>
          {rows.length > 0 && <span className="hint">click a folder to highlight it in the library</span>}</div>
        <div className="hov-band-row">
          <div className="hov-fchips">
            {rows.length ? rows.map(function(row){
              var feat = row[0], vids = row[1];
              return (
                <div key={feat} className={'hov-fchip' + (props.focusFeature === feat ? ' on' : '')}
                     onClick={function(){ props.onFocusFeature(props.focusFeature === feat ? null : feat); }}>
                  <b>{feat}/</b><span>{vids.length} · {exportPrefix(vids[0], props.name)}_01.mp4{vids.length > 1 ? ' …' : ''}</span>
                </div>
              );
            }) : <span className="hov-fchips-empty">Tag the videos above to build the folders.</span>}
          </div>
          <button className="hov-export-btn" disabled={!props.canExport} onClick={props.onExport}>{exportLabel}</button>
        </div>
        {ex.running && <ExportRunBlock ex={ex}/>}
        {!ex.running && ex.msg && <div className="hov-export-msg">{ex.msg}</div>}
      </div>
    </>
  );
}

// Per-video export failures: the real cause, "Re-sync this video" when Drive said not found,
// and one "Retry failed" for the rest (the selection is already reduced to what failed).
var CAUSE_LABELS = { not_found: 'Not found', unsupported: 'Unsupported type', drive_http: 'Drive error', timeout: 'Timeout', network: 'Network', disk: 'Disk', mark: 'Not marked' };
function ExportFailures({ failed, canRetry, onRetry, onResync }) {
  var retryable = failed.filter(function(x){ return x.resync !== 'gone' && x.cause !== 'unsupported' && x.cause !== 'mark'; }).length;
  return (
    <div className="hov-export-fail">
      <div className="hov-export-fail-head">
        <span>{failed.length} failed</span>
        {retryable > 0 && <button className="hov-retry-btn" disabled={!canRetry} onClick={onRetry}
          title={canRetry ? 'Export only the videos that failed' : 'Tag the remaining videos first'}>Retry failed ({retryable})</button>}
      </div>
      <ul>
        {failed.slice(0, 12).map(function(x){
          var nf = x.cause === 'not_found' && x.resync !== 'gone' && x.resync !== 'found';
          return (
            <li key={x.id}>
              <span className={'hov-fail-cause ' + x.cause}>{x.resync === 'gone' ? 'Gone' : x.resync === 'found' ? 'Found' : (CAUSE_LABELS[x.cause] || x.cause)}</span>
              <span className="hov-fail-what"><b>{x.creator && x.creator !== 'unknown' ? x.creator : '?'}</b> · {String(x.name || x.id).slice(0, 40)}<br/><span className="dim">{x.error}</span></span>
              {nf && <button className="hov-resync-btn" disabled={x.resync === 'running'} onClick={function(){ onResync(x.id); }}>{x.resync === 'running' ? 'Checking…' : 'Re-sync this video'}</button>}
            </li>
          );
        })}
      </ul>
      {failed.length > 12 && <div className="dim">+{failed.length - 12} more</div>}
    </div>
  );
}

function ThumbCard({ file, active, selected, dim, onClick }) {
  var ts=useState(true); var thumbOk=ts[0]; var setThumbOk=ts[1];
  var apiUrl=process.env.REACT_APP_API_URL||'';
  var st=dotState(file);
  var cls='hov-tc'+(active?' active':'')+(selected?' selected':'')+(st==='done'?' exported':'')+(dim?' dim':'');
  var unresolved=!file.creator||file.creator==='unknown';
  var src=file.featureSource;
  return (
    <div className={cls} onClick={onClick}
         title={(unresolved?'Creator not resolved':file.creator)+' · '+(file.feature ? file.feature + (src==='detected'?' (from the filename)':' (set by you)') : 'no feature')+' · '+(st==='done'?'exported':'not exported')}>
      {thumbOk?<img src={apiUrl+'/api/hov/thumb/'+file.id} alt="" loading="lazy" onError={function(){ setThumbOk(false); }} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
        :<div className="hov-tc-fallback">&#9654;</div>}
      <span className={'hov-tc-who'+(unresolved?' unresolved':'')}>{firstName(file.creator)}</span>
      <span className={'hov-tc-feat '+(file.feature ? (src==='detected'?'detected':'set') : 'none')}>{file.feature || 'no feature'}</span>
      {selected
        ? <span className="hov-tc-tick">&#10003;</span>
        : <span className={'hov-dot hov-tc-dot '+st}/>}
    </div>
  );
}

// Thousands of thumbnails can be loaded (infinite scroll): only re-render a card when its own file,
// active or selected state changes. Its handlers only use functional state updates, so a handler
// from an earlier render is still correct.
var ThumbCardMemo = React.memo(ThumbCard, function(a, b){
  return a.file === b.file && a.active === b.active && a.selected === b.selected && a.dim === b.dim;
});

function PlayerPanel({ file }) {
  var ps=useState(false); var playing=ps[0]; var setPlaying=ps[1];
  var ts=useState(true); var thumbOk=ts[0]; var setThumbOk=ts[1];
  React.useEffect(function(){ setPlaying(false); setThumbOk(true); },[file.id]);
  var apiUrl=process.env.REACT_APP_API_URL||'';
  var driveUrl='https://drive.google.com/file/d/'+file.id+'/view';
  var streamSrc=apiUrl+'/api/hov/stream/'+file.id;
  var unresolved=!file.creator||file.creator==='unknown';
  return (
    <div className="hov-player">
      {playing?(
        <>
          <video src={streamSrc} controls autoPlay playsInline className="hov-player-video" onError={function(){ setPlaying(false); }}/>
          <button className="hov-stop-btn" onClick={function(){ setPlaying(false); }}>&#10005;</button>
          <a href={driveUrl} target="_blank" rel="noreferrer" className="hov-popout-btn">&#8599;</a>
        </>
      ):(
        <div className="hov-player-poster" onClick={function(){ setPlaying(true); }}>
          {thumbOk?<img src={apiUrl+'/api/hov/thumb/'+file.id} alt="" onError={function(){ setThumbOk(false); }} className="hov-player-img"/>
            :<div className="hov-thumb-fallback" style={{fontSize:48}}>&#9654;</div>}
          <div className="hov-play-overlay"><div className="hov-play-btn"><div className="hov-play-tri"/></div></div>
          <div className="hov-player-top">
            <b>{unresolved ? 'Creator not resolved' : file.creator}</b>
            <span className={'hov-player-tag' + (file.feature ? '' : ' none')}>{file.feature || 'no feature'}</span>
            <span className="hov-player-st">{file.exportedAt ? 'exported' : 'to process'}</span>
          </div>
          <div className="hov-player-bot"><span>{stampLabel(file.addedAt || file.createdTime)}</span><span>{file.size}</span></div>
          <a href={driveUrl} target="_blank" rel="noreferrer" className="hov-popout-btn" onClick={function(e){ e.stopPropagation(); }}>&#8599;</a>
        </div>
      )}
    </div>
  );
}
